// THE CALENDAR'S LOOK, for every calendar of the three apps — this folder is its one home; no app restyles a
// calendar of its own. Tokens carry what PrimeNG exposes as one; the `css` callback at the foot carries the rest
// (PrimeNG fixes the day numbers at 1rem and leaves the weekday row, the title buttons, the inline panel, the
// neighbouring months' days and a popup's width without a token). The callback is emitted after PrimeNG's own
// calendar sheet, so a rule here wins at equal weight.
//
// Three things hold the look together, and each has a reason:
//   - the tints are mixed from `{primary.color}`, never taken from a primary palette step or a highlight token:
//     the semantic palette maps each step onto a primitive of the same name, a browser computes that
//     self-reference as empty, and the light theme's highlight tokens point at those steps — a tint that leaned
//     on either painted nothing;
//   - today is marked by a dot under its number, never by a filled disc: the dark surface ramp ascends, so a
//     high "surface" step is a LIGHT grey there;
//   - the picked day keeps its number in the text colour (white on the brand orange measures 3.8 : 1).

/** The tint under the picked day and behind a picked range, per colour scheme (the same paint in both uses). */
const tint = (share: number): string => `color-mix(in srgb, {primary.color} ${share}%, transparent)`;

export const root = {
    transitionDuration: '{transition.duration}'
};

// A popup calendar is one of the app's popups: the frosted popover surface at the panel radius (the blur itself
// is each app's global rule over the popups it mounts). An inline calendar draws no card — see the callback.
export const panel = {
    background: '{overlay.popover.background}',
    borderColor: '{overlay.popover.border.color}',
    color: '{content.color}',
    borderRadius: '{overlay.popover.border.radius}',
    shadow: '{overlay.popover.shadow}',
    padding: '0.75rem'
};

export const header = {
    background: 'transparent',
    borderColor: 'transparent',
    color: '{content.color}',
    padding: '0 0 0.375rem 0',
    gap: '0.125rem'
};

// `minWidth` holds the widest month name with its year, so the two arrows keep their place from month to month.
// The title reads at the day numbers' size and is told from them by its weight.
export const title = {
    gap: '0',
    fontWeight: '600',
    fontSize: '0.8125rem',
    minWidth: '8.75rem'
};

// The calendar button at the end of a date field is a quiet icon, the way a dropdown's arrow is: no fill at rest,
// the app's hover fill under the pointer. One value serves both themes — PrimeNG's own dark values are high steps
// of the surface ramp, which ascends in this theme, and drew a light grey block at the end of a dark field. The
// icon takes the second text colour: the field-icon colour measures 2.3 : 1 on a white field.
export const dropdown = {
    width: '2.5rem',
    sm: {
        width: '2rem'
    },
    lg: {
        width: '3rem'
    },
    background: 'transparent',
    hoverBackground: '{surface.hover}',
    activeBackground: '{surface.active}',
    color: '{text.secondary.color}',
    hoverColor: '{text.color}',
    activeColor: '{text.color}',
    borderColor: '{form.field.border.color}',
    hoverBorderColor: '{form.field.border.color}',
    activeBorderColor: '{form.field.border.color}',
    borderRadius: '{form.field.border.radius}',
    focusRing: {
        width: '{form.field.focus.ring.width}',
        style: '{form.field.focus.ring.style}',
        color: '{form.field.focus.ring.color}',
        offset: '{form.field.focus.ring.offset}',
        shadow: '{form.field.focus.ring.shadow}'
    }
};

export const inputIcon = {
    color: '{form.field.icon.color}'
};

export const selectMonth = {
    hoverBackground: '{surface.hover}',
    color: '{content.color}',
    hoverColor: '{content.hover.color}',
    padding: '0.1875rem 0.3125rem',
    borderRadius: '{content.border.radius}'
};

export const selectYear = {
    hoverBackground: '{surface.hover}',
    color: '{content.color}',
    hoverColor: '{content.hover.color}',
    padding: '0.1875rem 0.3125rem',
    borderRadius: '{content.border.radius}'
};

export const group = {
    borderColor: '{content.border.color}',
    gap: '{overlay.popover.padding}'
};

// The day numbers at the app's small-text size (PrimeNG's sheet fixes the table at 1rem).
export const dayView = {
    margin: '0',
    fontSize: '0.8125rem'
};

// The weekday row in the register the Campaigns calendar draws its own in — small, uppercase, spaced — on the
// second text colour: it tells which column is which day, and the muted colour measures 2.3 : 1 on white.
export const weekDay = {
    padding: '0 0 0.25rem 0',
    fontWeight: '500',
    color: '{text.secondary.color}',
    fontSize: '0.6875rem',
    letterSpacing: '0.04em'
};

// A day is as tall as the app's other controls (2rem). The picked day is an outline over a tint; a range is one
// band behind its row (the callback paints it), so the days inside a range carry no fill of their own.
export const date = {
    hoverBackground: '{surface.hover}',
    rangeSelectedBackground: 'transparent',
    color: '{content.color}',
    hoverColor: '{content.hover.color}',
    selectedColor: '{text.color}',
    rangeSelectedColor: '{text.color}',
    selectedBorderColor: '{primary.color}',
    selectedFontWeight: '600',
    disabledOpacity: '0.32',
    width: '2rem',
    height: '2rem',
    borderRadius: '50%',
    padding: '0.125rem',
    focusRing: {
        width: '{form.field.focus.ring.width}',
        style: '{form.field.focus.ring.style}',
        color: '{form.field.focus.ring.color}',
        offset: '{form.field.focus.ring.offset}',
        shadow: '{form.field.focus.ring.shadow}'
    }
};

export const today = {
    background: 'transparent',
    color: '{content.color}',
    markerColor: '{primary.color}',
    markerSize: '0.25rem'
};

// `minWidth` is the day grid's own width — seven days of `date.width` with `date.padding` either side — and the
// year view takes the same: neither view holds a table, so without a floor a calendar changed width (an inline
// one grew to a single long line of month names) the moment its title was clicked.
export const monthView = {
    margin: '0.25rem 0 0 0',
    minWidth: '15.75rem'
};

export const month = {
    padding: '0.4375rem 0',
    borderRadius: '999px',
    fontSize: '0.8125rem'
};

export const yearView = {
    margin: '0.25rem 0 0 0'
};

export const year = {
    padding: '0.4375rem 0',
    borderRadius: '999px',
    fontSize: '0.8125rem'
};

export const buttonbar = {
    padding: '0.5rem 0 0 0',
    borderColor: '{content.border.color}'
};

export const timePicker = {
    padding: '0.5rem 0 0 0',
    borderColor: '{content.border.color}',
    gap: '0.5rem',
    buttonGap: '0.25rem',
    fontSize: '0.875rem'
};

export const colorScheme = {
    light: {
        date: {
            selectedBackground: tint(9),
            rangeBandBackground: tint(9)
        }
    },
    dark: {
        date: {
            selectedBackground: tint(16),
            rangeBandBackground: tint(16)
        }
    }
};

// Three rules below answer something PrimeNG does to every calendar, and each is here for that reason alone:
//   - A POPUP KEEPS ITS OWN WIDTH. PrimeNG wraps the panel in an element it makes at least as wide as the date
//     field (a width it writes on that element), and the panel filled it — under a full-width field in a dialog the
//     compact day grid was spread across the whole field. The panel takes its own width inside the wrapper;
//     `min-width: 0` answers PrimeNG's own 100% floor, which still binds a popup that opens in place. The wrapper's
//     empty part then lets the pointer through: PrimeNG counts a click on it as inside the popup, so a click beside
//     the calendar neither closed it nor reached what lies under it.
//   - A MONTH SHOWS ITS OWN DAYS ALONE. The days of the neighbouring months are hidden here, never by PrimeNG's
//     input for it: with that input off the cells render empty, and its arrow-key handlers read the day they
//     expect inside one. A hidden day keeps its cell, so the grid keeps its shape.
//   - AN INLINE CALENDAR DRAWS NO CARD, and its panel must not clip: with the padding gone, PrimeNG's own
//     `overflow-x: auto` would cut the focus ring of a day at the grid's edge.
//   - A CALENDAR'S FOOT CARRIES CLEAR ALONE. PrimeNG's footer is Today and Clear together, with no input to drop
//     one, and its Today ignores a field's limits: it sets today's moment whatever the minimum is, the field's own
//     bounds then move the value, and on an expiry field whose minimum is "now" the result is a moment the server
//     refuses. Today is not drawn; Clear keeps the place it had beside it, at the foot's end.
export const css = ({dt}: {dt: (token: string) => string}) => `
.p-datepicker-day-view {
    font-size: ${dt('datepicker.day.view.font.size')};
}
.p-datepicker-day {
    font-variant-numeric: tabular-nums;
}
.p-datepicker-weekday {
    font-size: ${dt('datepicker.week.day.font.size')};
    letter-spacing: ${dt('datepicker.week.day.letter.spacing')};
    text-transform: uppercase;
}
.p-datepicker-header {
    justify-content: center;
    gap: ${dt('datepicker.header.gap')};
}
.p-datepicker-title {
    min-width: ${dt('datepicker.title.min.width')};
    justify-content: center;
    font-size: ${dt('datepicker.title.font.size')};
}
.p-datepicker-select-month,
.p-datepicker-select-year {
    font-family: inherit;
    font-size: inherit;
}
.p-datepicker-month-view,
.p-datepicker-year-view {
    display: grid;
    min-width: ${dt('datepicker.month.view.min.width')};
}
.p-datepicker-month-view {
    grid-template-columns: repeat(3, 1fr);
}
.p-datepicker-year-view {
    grid-template-columns: repeat(2, 1fr);
}
.p-datepicker-month {
    width: auto;
    font-size: ${dt('datepicker.month.font.size')};
}
.p-datepicker-year {
    width: auto;
    font-size: ${dt('datepicker.year.font.size')};
}
.p-datepicker-time-picker span {
    font-size: ${dt('datepicker.time.picker.font.size')};
    font-variant-numeric: tabular-nums;
}
.p-datepicker-calendar-container + .p-datepicker-time-picker,
.p-datepicker-buttonbar {
    margin-top: 0.5rem;
}
.p-datepicker-buttonbar {
    justify-content: flex-end;
}
.p-datepicker-buttonbar .p-datepicker-today-button {
    display: none;
}
.p-datepicker-panel-inline {
    background: none;
    border: 0 none;
    border-radius: 0;
    padding: 0;
    overflow: visible;
}
.p-datepicker-panel:not(.p-datepicker-panel-inline) {
    width: fit-content;
    min-width: 0;
    pointer-events: auto;
}
.p-motion:has(> .p-datepicker-panel:not(.p-datepicker-panel-inline)) {
    pointer-events: none;
}
.p-datepicker-other-month > .p-datepicker-day {
    visibility: hidden;
}
.p-datepicker-day.p-disabled {
    opacity: ${dt('datepicker.date.disabled.opacity')};
}
.p-datepicker-day-selected {
    border-color: ${dt('datepicker.date.selected.border.color')};
    font-weight: ${dt('datepicker.date.selected.font.weight')};
}
.p-datepicker-month-selected,
.p-datepicker-year-selected {
    box-shadow: inset 0 0 0 1px ${dt('datepicker.date.selected.border.color')};
    font-weight: ${dt('datepicker.date.selected.font.weight')};
}
.p-datepicker-today > .p-datepicker-day::after {
    content: '';
    position: absolute;
    bottom: 0.1875rem;
    left: calc(50% - ${dt('datepicker.today.marker.size')} / 2);
    width: ${dt('datepicker.today.marker.size')};
    height: ${dt('datepicker.today.marker.size')};
    border-radius: 50%;
    background: ${dt('datepicker.today.marker.color')};
}
.p-datepicker-day-cell:has(> .p-datepicker-day-selected-range) {
    background: linear-gradient(${dt('datepicker.date.range.band.background')}, ${dt('datepicker.date.range.band.background')}) center / 100% ${dt('datepicker.date.height')} no-repeat;
}
.p-datepicker-day-cell:has(> .p-datepicker-day-selected):has(+ .p-datepicker-day-cell > .p-datepicker-day-selected-range) {
    background: linear-gradient(${dt('datepicker.date.range.band.background')}, ${dt('datepicker.date.range.band.background')}) right center / 50% ${dt('datepicker.date.height')} no-repeat;
}
.p-datepicker-day-cell:has(> .p-datepicker-day-selected-range) + .p-datepicker-day-cell:has(> .p-datepicker-day-selected) {
    background: linear-gradient(${dt('datepicker.date.range.band.background')}, ${dt('datepicker.date.range.band.background')}) left center / 50% ${dt('datepicker.date.height')} no-repeat;
}
.p-datepicker-panel .p-datepicker-header .p-button.p-button-icon-only {
    width: 1.75rem;
    height: 1.75rem;
    padding: 0;
}
.p-datepicker-panel .p-datepicker-header .p-button .p-icon {
    width: 0.75rem;
    height: 0.75rem;
}
.p-datepicker-panel .p-datepicker-time-picker .p-button.p-button-icon-only {
    width: 1.5rem;
    height: 1.5rem;
    padding: 0;
}
.p-datepicker-panel .p-datepicker-time-picker .p-button .p-icon {
    width: 0.6875rem;
    height: 0.6875rem;
}
`;

export default {
    root,
    panel,
    header,
    title,
    dropdown,
    inputIcon,
    selectMonth,
    selectYear,
    group,
    dayView,
    weekDay,
    date,
    today,
    monthView,
    month,
    yearView,
    year,
    buttonbar,
    timePicker,
    colorScheme,
    css
};
